import { useEffect, useState } from "react"
import Image from "next/image"
import type { CommentAttachment } from "@/schema"
import { Button, DialogDescription, DialogTitle } from "@ui/shad"
import { Download, File, X } from "lucide-react"

import { CustomDialog } from "@/components/dialogs/custom-dialog"
import { formatByteSize } from "@/components/intake-forms/format"

interface PendingCommentAttachmentsProps {
  files: File[]
  onRemove: (index: number) => void
  disabled?: boolean
}

interface PendingPreview {
  file: File
  url?: string
}

export function PendingCommentAttachments({ files, onRemove, disabled }: PendingCommentAttachmentsProps) {
  const [previews, setPreviews] = useState<PendingPreview[]>([])

  useEffect(() => {
    const nextPreviews = files.map((file) => ({
      file,
      url: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined,
    }))
    setPreviews(nextPreviews)

    return () => nextPreviews.forEach((preview) => preview.url && URL.revokeObjectURL(preview.url))
  }, [files])

  if (!previews.length) return null

  return (
    <div className="mt-2 flex flex-wrap gap-2" aria-label="Attachments to upload">
      {previews.map(({ file, url }, index) => (
        <div
          key={`${file.name}-${file.size}-${index}`}
          className="bg-muted relative flex max-w-48 items-center gap-2 rounded-lg border p-1.5 pr-7"
        >
          {url ? (
            <Image src={url} alt="" width={40} height={40} unoptimized className="size-10 rounded object-cover" />
          ) : (
            <div className="bg-background flex size-10 shrink-0 items-center justify-center rounded">
              <File className="text-muted-foreground size-4" />
            </div>
          )}
          <div className="min-w-0">
            <div className="truncate text-xs font-medium">{file.name}</div>
            <div className="text-muted-foreground text-xs">{formatByteSize(file.size)}</div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="iconXs"
            className="absolute top-1 right-1"
            onClick={() => onRemove(index)}
            disabled={disabled}
            aria-label={`Remove ${file.name}`}
          >
            <X />
          </Button>
        </div>
      ))}
    </div>
  )
}

export function CommentAttachments({ attachments }: { attachments: CommentAttachment[] }) {
  const [selectedImage, setSelectedImage] = useState<CommentAttachment | null>(null)

  if (!attachments.length) return null

  return (
    <>
      <div className="mt-2 flex flex-wrap gap-2" aria-label="Comment attachments">
        {attachments.map((attachment) => {
          const isImage = attachment.file_type.startsWith("image/")
          const viewUrl = attachment.view_url ?? attachment.download_url

          if (isImage) {
            return (
              <button
                key={attachment.download_url}
                type="button"
                onClick={() => setSelectedImage(attachment)}
                className="focus-visible:ring-ring overflow-hidden rounded-lg border focus-visible:ring-2 focus-visible:outline-none"
                aria-label={`Preview ${attachment.file_name}`}
              >
                <Image
                  src={viewUrl}
                  alt={attachment.file_name}
                  width={80}
                  height={80}
                  unoptimized
                  className="size-20 object-cover transition-transform hover:scale-105"
                />
              </button>
            )
          }

          return (
            <a
              key={attachment.download_url}
              href={attachment.download_url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-muted hover:bg-accent focus-visible:ring-ring flex max-w-64 items-center gap-2 rounded-lg border p-2 focus-visible:ring-2 focus-visible:outline-none"
            >
              <File className="text-muted-foreground size-4 shrink-0" />
              <span className="min-w-0">
                <span className="block truncate text-xs font-medium">{attachment.file_name}</span>
                <span className="text-muted-foreground block text-xs">
                  {formatByteSize(attachment.file_size)} · {attachment.file_type}
                </span>
              </span>
              <Download className="text-muted-foreground size-3.5 shrink-0" />
            </a>
          )
        })}
      </div>

      <CustomDialog
        open={selectedImage !== null}
        onOpenChange={(open) => !open && setSelectedImage(null)}
        title={<>Image preview</>}
        titleProvided
        customClassName={"max-w-4xl"}
        contentProps={{}}
      >
        <DialogTitle className="sr-only">Image preview</DialogTitle>
        <DialogDescription className="sr-only">Full-size preview of the attached image</DialogDescription>
        {selectedImage && (
          <Image
            src={selectedImage.view_url ?? selectedImage.download_url}
            alt={selectedImage.file_name}
            width={1200}
            height={900}
            unoptimized
            className="max-h-[75vh] w-full rounded-lg object-contain"
          />
        )}
      </CustomDialog>
    </>
  )
}
